import { getTranslation } from "@mivo/i18n";
import {
  getCachedChatAvailability,
  mapTempConversationId,
  persistConversations,
  replacePendingCachedMessage,
  upsertConversation,
  type CachedChatMessage,
  type CachedConversation,
} from "@/domains/offline/chat/chatCache";
import { pendingChatMessageId } from "@/domains/offline/chat/chatOutboxStore";
import { withChatHub } from "@/domains/offline/chat/chatHubManager";
import {
  getChatOutboxItem,
  incrementChatOutboxRetry,
  listPendingChatOutbox,
  markChatOutboxFailed,
  markChatOutboxHeld,
  markChatOutboxPending,
  removeChatOutbox,
  restoreChatOutbox,
} from "@/domains/offline/chat/chatOutboxStore";
import {
  emitChatConversationCreated,
  emitChatOutboxFlushed,
} from "@/domains/offline/chat/chatSyncEvents";
import { OFFLINE_TRANSLATIONS } from "@/domains/offline/constants";
import type { ChatOutboxEntry } from "@/domains/offline/db/schema";
import { getConnectivityOnline } from "@/domains/offline/connectivity/connectivityStore";
import { api } from "@/domains/shared/services/api";
import { HubConnectionState, type HubConnection } from "@microsoft/signalr";
import { toast } from "sonner";

const MAX_CHAT_RETRIES = 3;
const CHAT_PREFETCH_CONV_LIMIT = 50;

let flushChatOutboxChain: Promise<void> = Promise.resolve();

function runExclusiveChatFlush(task: () => Promise<void>): Promise<void> {
  const next = flushChatOutboxChain.then(task, task);
  flushChatOutboxChain = next.catch(() => {});
  return next;
}

type HubChatMessage = {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  createdAt: string;
};

type HubConversation = CachedConversation;

async function isMessagingAllowed(therapistId: string | undefined): Promise<boolean> {
  if (!therapistId) {
    return true;
  }

  if (getConnectivityOnline()) {
    try {
      const { data } = await api.get<{ messagingAllowed: boolean; message?: string | null }>(
        `/therapists/${therapistId}/chat-availability`,
      );
      const { persistChatAvailability } = await import("@/domains/offline/chat/chatCache");
      await persistChatAvailability(therapistId, data.messagingAllowed, data.message);
      return data.messagingAllowed;
    } catch {
      const cached = await getCachedChatAvailability(therapistId);
      return cached?.messagingAllowed ?? true;
    }
  }

  const cached = await getCachedChatAvailability(therapistId);
  return cached?.messagingAllowed ?? true;
}

async function replayChatOutboxItem(conn: HubConnection, item: ChatOutboxEntry): Promise<void> {
  const snapshot = await getChatOutboxItem(item.id);
  if (!snapshot) {
    return;
  }

  if (item.kind === "sendMessage") {
    const [conversationId, content] = item.args as [string, string];

    if (!(await isMessagingAllowed(item.therapistId))) {
      await markChatOutboxHeld(item.id, "outside_window");
      return;
    }

    await removeChatOutbox(item.id);

    try {
      const msg = await conn.invoke<HubChatMessage>("SendMessage", conversationId, content, item.id);
      await replacePendingCachedMessage(conversationId, pendingChatMessageId(item.id), msg);
    } catch (err) {
      await restoreChatOutbox(snapshot);
      throw err;
    }

    return;
  }

  if (item.kind === "markRead") {
    const [conversationId] = item.args as [string];
    await removeChatOutbox(item.id);

    try {
      await conn.invoke("MarkConversationRead", conversationId);
    } catch (err) {
      await restoreChatOutbox(snapshot);
      throw err;
    }

    return;
  }

  if (item.kind === "createConversation") {
    const [patientId, therapistId, type, tempId] = item.args as [string, string, string, string];

    if (!(await isMessagingAllowed(therapistId))) {
      await markChatOutboxHeld(item.id, "outside_window");
      return;
    }

    await removeChatOutbox(item.id);

    try {
      const conv = await conn.invoke<HubConversation>("CreateConversation", patientId, therapistId, type);
      await upsertConversation(conv);
      await mapTempConversationId(tempId, conv.id);
      emitChatConversationCreated({ tempId, conversation: conv as unknown as Record<string, unknown> });
    } catch (err) {
      await restoreChatOutbox(snapshot);
      throw err;
    }
  }
}

async function flushChatOutboxInner(): Promise<void> {
  if (!getConnectivityOnline()) {
    return;
  }

  const pending = await listPendingChatOutbox();
  if (pending.length === 0) {
    return;
  }

  await withChatHub(async (conn) => {
    if (conn.state !== HubConnectionState.Connected) {
      return;
    }

    let syncedSendCount = 0;
    const touchedConversations = new Set<string>();

    for (const item of pending) {
      if (item.status === "held") {
        await markChatOutboxPending(item.id);
      }

      try {
        const wasSend = item.kind === "sendMessage";
        await replayChatOutboxItem(conn, item);
        if (wasSend) {
          syncedSendCount += 1;
          const conversationId = String(item.conversationId ?? item.args[0] ?? "");
          if (conversationId) {
            touchedConversations.add(conversationId);
          }
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : getTranslation(OFFLINE_TRANSLATIONS.chatSyncFailed);
        const isWindow =
          msg.toLowerCase().includes("horário") ||
          msg.toLowerCase().includes("horario") ||
          msg.toLowerCase().includes("disponível");

        if (isWindow && item.kind !== "markRead") {
          await markChatOutboxHeld(item.id, "outside_window");
          continue;
        }

        if (item.retries + 1 >= MAX_CHAT_RETRIES) {
          await markChatOutboxFailed(item.id, msg);
          toast.error(
            getTranslation(OFFLINE_TRANSLATIONS.chatSyncFailedWithReason).replace("{reason}", msg),
          );
        } else {
          await incrementChatOutboxRetry(item.id);
        }
      }
    }

    if (syncedSendCount > 0) {
      toast.success(
        syncedSendCount === 1
          ? getTranslation(OFFLINE_TRANSLATIONS.messageSent)
          : getTranslation(OFFLINE_TRANSLATIONS.messagesSentCount).replace("{count}", String(syncedSendCount)),
      );
      emitChatOutboxFlushed([...touchedConversations]);
    }
  });
}

export function flushChatOutbox(): Promise<void> {
  return runExclusiveChatFlush(flushChatOutboxInner);
}

export async function pullChatScope(): Promise<void> {
  if (!getConnectivityOnline()) {
    return;
  }

  await withChatHub(async (conn) => {
    const conversations = await conn.invoke<HubConversation[]>("GetConversations", "family");
    await persistConversations(conversations);

    const therapistIds = new Set(conversations.map((c) => c.therapistId));
    const { persistChatAvailability } = await import("@/domains/offline/chat/chatCache");

    for (const therapistId of therapistIds) {
      try {
        const { data } = await api.get<{ messagingAllowed: boolean; message?: string | null }>(
          `/therapists/${therapistId}/chat-availability`,
        );
        await persistChatAvailability(therapistId, data.messagingAllowed, data.message);
      } catch {
        /* best-effort */
      }
    }

    const slice = conversations.slice(0, CHAT_PREFETCH_CONV_LIMIT);
    for (const conv of slice) {
      try {
        const messages = await conn.invoke<HubChatMessage[]>("GetMessages", conv.id);
        const { persistServerConversationMessages } = await import("@/domains/offline/chat/chatCache");
        await persistServerConversationMessages(conv.id, messages);
      } catch {
        /* skip single thread */
      }
    }
  });
}
